import React, { useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  PageHeader,
  Popconfirm,
  Space,
  Tabs,
  Tag,
  Typography,
  notification,
} from 'antd';
import { Content } from 'antd/lib/layout/layout';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useApproveBuyerMutation,
  useBlockBuyerMutation,
  useDisableBuyerMutation,
  useDeleteBuyerMutation,
  useEnableBuyerMutation,
  useGetBuyerQuery,
} from '../../../app/api/userApiSlice';
import Spinner from '../../../components/common/Spinner';
import BackButton from '../../../components/common/BackButton';
import EditBuyerForm from '../containers/EditBuyerForm';
import ViewBuyerVehicles from '../containers/ViewBuyerVehicles';

const ViewBuyer: React.FC = () => {
  const navigate = useNavigate();
  const { buyerId } = useParams<{ buyerId: any }>();
  const { data: buyer, isLoading } = useGetBuyerQuery(buyerId);
  const [approveBuyer, { isLoading: loadingApproveBuyer }] = useApproveBuyerMutation();
  const [blockBuyer, { isLoading: loadingBlockBuyer }] = useBlockBuyerMutation();
  const [enableBuyer, { isLoading: loadingEnableBuyer }] = useEnableBuyerMutation();
  const [disableBuyer, { isLoading: loadingDisableBuyer }] = useDisableBuyerMutation();
  const [deleteBuyer, { isLoading: loadingDeleteBuyer }] = useDeleteBuyerMutation();

  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleApproveBuyer = (buyerId: string): void => {
    void (async () => {
      try {
        await approveBuyer({ id: buyerId });

        notification.success({
          message: 'Success',
          description: 'Buyer approved successfully',
        });

        navigate(-1);
      } catch (error) {
        console.log(error)
      }
    })();
  };

  const handleRejectBuyer = (buyerId: string): void => {
    void (async () => {
      try {
        await blockBuyer({ id: buyerId });
        notification.success({
          message: 'Success',
          description: 'Buyer blocked successfully',
        });

        navigate(-1);
      } catch (error: any) {
        console.log(error)
      }
    })();
  };

  const handleEnableBuyer = (): void => {
    void (async () => {
      if (!buyer) return;

      try {
        await enableBuyer(buyer.id);
        notification.success({
          message: 'Success',
          description: 'Buyer enabled successfully',
        });
      } catch (error: any) {
        console.log(error)
      }
    })();
  };

  const handleDisableBuyer = (): void => {
    void (async () => {
      if (!buyer) return;

      try {
        await disableBuyer(buyer.id);
        notification.success({
          message: 'Success',
          description: 'Buyer disabled successfully',
        });
      } catch (error: any) {
        console.log(error)
      }
    })();
  };

  const handleDeleteBuyer = (): void => {
    void (async () => {
      if (!buyer) return;

      try {
        await deleteBuyer(buyer.id);
        notification.success({
          message: 'Success',
          description: 'Buyer account deleted successfully',
        });
      } catch (error: any) {
        console.log(error)
      }
    })();
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!buyer) {
    return <div>Buyer not found!</div>;
  }

  return (
    <>
      <BackButton />
      <PageHeader
        className='site-page-header-responsive'
        title={buyer?.name}
        subTitle={
          <Space>
            <Typography.Text strong>Buyer</Typography.Text>

            {buyer?.status.id === 1 ? (
              <Tag color='volcano'>Pending</Tag>
            ) : buyer?.status.id === 2 ? (
              <>
                <Tag color='green'>Approved</Tag>
                {buyer?.isDisable ? (
                  <Tag color='magenta'>Deactivated</Tag>
                ) : (
                  <Tag color='blue'>Active</Tag>
                )}
              </>
            ) : (
              <Tag color='red'>Rejected</Tag>
            )}
          </Space>
        }
        extra={
          buyer &&
          buyer?.status.id === 1 && [
            <Button
              key='approve'
              type='primary'
              onClick={() => handleApproveBuyer(buyer.id)}
              loading={loadingApproveBuyer}
            >
              Approve Buyer
            </Button>,
            <Button
              key='reject'
              type='ghost'
              danger
              onClick={() => handleRejectBuyer(buyer.id)}
              loading={loadingBlockBuyer}
            >
              Reject Buyer
            </Button>,
          ]
        }
      >
        <Content>
          <Tabs
            defaultActiveKey='details'
            size='small'
            type='card'
            items={[
              {
                label: 'Details',
                key: 'details',
                children: (
                  <Descriptions
                    layout='horizontal'
                    size='middle'
                    column={1}
                    bordered
                    extra={
                      <Space>
                        {buyer && buyer?.status.id === 2 ? (
                          <>
                            {buyer.isDisable ? (
                              <Button
                                type='primary'
                                ghost
                                onClick={() => handleEnableBuyer()}
                                loading={loadingEnableBuyer}
                              >
                                Activate
                              </Button>
                            ) : (
                              <Button
                                type='primary'
                                ghost
                                onClick={() => handleDisableBuyer()}
                                loading={loadingDisableBuyer}
                              >
                                Deactivate
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            {buyer && buyer?.status.id === 3 && (
                              <Popconfirm
                                title={
                                  <p>
                                    Are you sure you want to delete this buyer account?
                                    <br />
                                    This action cannot be undone.
                                  </p>
                                }
                                placement='topLeft'
                                onConfirm={(event) => {
                                  handleDeleteBuyer();
                                }}
                                okText='Yes'
                                okButtonProps={{ danger: true }}
                              >
                                <Button type='primary' ghost danger loading={loadingDeleteBuyer}>
                                  Delete
                                </Button>
                              </Popconfirm>
                            )}
                          </>
                        )}
                        <Button type='primary' onClick={() => setDrawerVisible(true)}>
                          Edit
                        </Button>
                      </Space>
                    }
                  >
                    <Descriptions.Item label={<Typography.Text strong>Email</Typography.Text>}>
                      {buyer?.email}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={<Typography.Text strong>Phone Number</Typography.Text>}
                    >
                      {buyer?.phoneNumber}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={<Typography.Text strong>Address Line 1</Typography.Text>}
                    >
                      {buyer?.addressLine1}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={<Typography.Text strong>Address Line 2</Typography.Text>}
                    >
                      {buyer?.addressLine2}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Typography.Text strong>City</Typography.Text>}>
                      {buyer?.city}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={<Typography.Text strong>Postal Code</Typography.Text>}
                    >
                      {buyer?.postalCode}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={<Typography.Text strong>Buyer Category</Typography.Text>}
                    >
                      {buyer?.buyerCategory.category}
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                label: 'Vehicles',
                key: 'vehicles',
                children: (
                  <Card>
                    <ViewBuyerVehicles buyerId={buyer.id} />
                  </Card>
                ),
              },
            ]}
          />
        </Content>
        <Drawer
          title='Update Buyer Details'
          width={600}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          bodyStyle={{ paddingBottom: 80 }}
          extra={
            <Space>
              <Button onClick={() => setDrawerVisible(false)}>Cancel</Button>
              <Button type='primary' form='updateBuyerForm' key='submit' htmlType='submit'>
                Submit
              </Button>
            </Space>
          }
        >
          <EditBuyerForm
            buyer={buyer}
            onSuccessfulSubmit={() => setDrawerVisible(false)}
            drawerVisible={drawerVisible}
          />
        </Drawer>
      </PageHeader>
    </>
  );
};

export default ViewBuyer;
